import React from 'react';
import { Box, Drawer, Flex, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { UpcomingEventsList } from 'src/meetings/home/UpcomingEventsList';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';
import { HEADER_HEIGHT } from 'src/UI/app-container/header/Header';
import { EventDetailPanel } from 'src/meetings/home/EventDetailPanel';
import { MeetingDetailsContent } from 'src/meetings/detail/MeetingDetailsContent';
import { useSidebarMenu } from 'src/UI/page-nav/MenuTriggerSlot';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FamilyMeetingsPanel } from 'src/non-financial-data/v2/family/FamilyMeetingsPanel';

const ClientMeetingsTabs = {
  Upcoming: 'upcoming',
  Past: 'past',
} as const;

export default function ClientMeetingsContent() {
  const { clientId } = useCurrentClient();
  const { flagEnabledIndividualMeetingsRework } = useFlags();
  const { sectionSlug } = useParams<{ sectionSlug?: string }>();

  if (flagEnabledIndividualMeetingsRework) {
    return <FamilyMeetingsPanel scope={{ kind: 'individual', clientId }} section={sectionSlug} />;
  }

  return <LegacyClientMeetingsContent />;
}

function LegacyClientMeetingsContent() {
  const { clientId } = useCurrentClient();
  const { flagEnabledIndividualDetailsRework } = useFlags();
  const { sectionSlug, selectedEventId, eventTab } = useParams<{
    sectionSlug: string;
    selectedEventId?: string;
    eventTab?: string;
  }>();
  const pastOrUpcoming = sectionSlug === ClientMeetingsTabs.Past ? 'PAST' : 'UPCOMING';
  const { events, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } = useCalendarEvents({
    pastOrUpcoming,
    clientId,
  });
  const navigate = useNavigate();

  const selectedEvent = events.find(e => e.id === selectedEventId);

  const handlePastEventSelect = (eventId: string | null) => {
    if (!eventId) {
      return;
    }
    navigate(`/client/${clientId}/meetings/past/${eventId}/summary`);
  };

  const handleUpcomingEventSelect = (eventId: string | null) => {
    if (!eventId) {
      return;
    }
    navigate(`/client/${clientId}/meetings/upcoming/${eventId}`);
  };

  const handleUnselectEvent = () => {
    navigate(`/client/${clientId}/meetings/upcoming`);
  };

  const DATA_LINKS = [
    {
      label: 'Upcoming',
      href: `/client/${clientId}/meetings/${ClientMeetingsTabs.Upcoming}`,
    },
    {
      label: 'Past meetings',
      href: `/client/${clientId}/meetings/${ClientMeetingsTabs.Past}`,
    },
  ];
  const content = (() => {
    switch (sectionSlug) {
      case ClientMeetingsTabs.Upcoming:
        return (
          <Stack gap="sm">
            <MeetingsBackRow label="Upcoming" />
            <UpcomingEventsList
              events={events}
              selectedEventId={selectedEventId || null}
              onEventSelect={handleUpcomingEventSelect}
              onRefetchNeeded={refetch}
              hasNextPage={!!hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />
          </Stack>
        );
      case ClientMeetingsTabs.Past:
        return (
          <Stack gap="sm">
            <MeetingsBackRow label="Past meetings" />
            <UpcomingEventsList
              events={events}
              selectedEventId={selectedEventId || null}
              onEventSelect={handlePastEventSelect}
              onRefetchNeeded={refetch}
              hasNextPage={!!hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              order={'desc'}
            />
          </Stack>
        );
      default:
        return (
          <Navigate to={`/client/${clientId}/meetings/${ClientMeetingsTabs.Upcoming}`} replace />
        );
    }
  })();

  return (
    <Flex direction={'row'} justify={'stretch'}>
      <Box flex={1}>
        <Drawer
          opened={!!selectedEventId}
          onClose={handleUnselectEvent}
          withCloseButton={false}
          position="bottom"
          size="md"
          styles={{
            content: {
              top: HEADER_HEIGHT,
              height: `calc(100% - ${HEADER_HEIGHT}px)`,
              display: 'flex',
              flexDirection: 'column',
            },
            body: {
              paddingLeft: 54,
              paddingRight: 54,
            },
          }}
        >
          {selectedEventId &&
            (pastOrUpcoming === 'UPCOMING' ? (
              <EventDetailPanel selectedEvent={selectedEvent} onCloseEvent={handleUnselectEvent} />
            ) : (
              <MeetingDetailsContent
                eventId={selectedEventId}
                onDelete={() => {}}
                basePath={`/client/${clientId}/meetings/past/${selectedEventId}`}
                onClose={handleUnselectEvent}
                tab={eventTab}
              />
            ))}
        </Drawer>
        <PageWithSidebar
          links={DATA_LINKS}
          activeUrl={`/client/${clientId}/meetings/${sectionSlug}`}
          variant={flagEnabledIndividualDetailsRework ? 'profileV2' : 'default'}
        >
          {content}
        </PageWithSidebar>
      </Box>
    </Flex>
  );
}

function MeetingsBackRow({ label }: { label: string }) {
  const { open, isCollapsed } = useSidebarMenu({ customTrigger: true });

  if (!isCollapsed) return null;

  return (
    <UnstyledButton
      onClick={open}
      aria-haspopup="dialog"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <IconChevronLeft size={16} color="var(--mantine-color-gray-8)" />
      <Text
        size="md"
        style={{
          color: 'var(--mantine-color-gray-8)',
          fontWeight: FONT_WEIGHTS.medium,
        }}
      >
        {label}
      </Text>
    </UnstyledButton>
  );
}
